<template>
  <div class="pay-dialogs">
    <!-- 确认支付（微信回调未成功） -->
    <el-dialog
      ref="confirmDialog"
      :visible.sync="confirmVisibleSync"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="pro-dialog"
      width="431px"
      append-to-body
    >
      <div class="pro-dialog-body">
        <div class="pro-dialog-title">{{ text.confirmPay }}</div>
        <div class="pro-dialog-desc">{{ text.confirmPayDesc }}</div>
        <div class="pro-dialog-foot">
          <span class="btn-pill btn-outline" @click="onCancel">{{
            text.cancel
          }}</span>
          <span class="btn-pill btn-primary" @click="$emit('paid')">{{
            text.paid
          }}</span>
        </div>
      </div>
    </el-dialog>

    <!-- 正在查询支付结果（点「我已支付」后展示） -->
    <el-dialog
      ref="queryDialog"
      :visible.sync="queryVisibleSync"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="pro-dialog"
      width="431px"
      append-to-body
    >
      <div class="pro-dialog-body query-body">
        <img class="query-spinner" :src="spinnerIcon" alt="" />
        <div class="query-title">{{ text.queryingPayResult }}</div>
      </div>
    </el-dialog>

    <!-- 支付失败 -->
    <el-dialog
      ref="failedDialog"
      :visible.sync="failedVisibleSync"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="pro-dialog"
      width="431px"
      append-to-body
    >
      <div class="pro-dialog-body center-body">
        <img class="fail-icon" :src="failedIcon" alt="" />
        <div class="pro-dialog-title center">{{ text.payFailed }}</div>
        <div class="pro-dialog-desc center">{{ text.payFailedDesc }}</div>
        <div class="pro-dialog-foot">
          <span class="btn-pill btn-outline" @click="$emit('view-order')">{{
            text.viewOrder
          }}</span>
          <span class="btn-pill btn-primary" @click="$emit('repay')">{{
            text.repay
          }}</span>
        </div>
      </div>
    </el-dialog>

    <!-- 有未支付订单提示（套餐购买页） -->
    <el-dialog
      ref="unpaidDialog"
      :visible.sync="unpaidVisibleSync"
      :show-close="false"
      :close-on-click-modal="false"
      custom-class="pro-dialog"
      width="431px"
      append-to-body
    >
      <div class="pro-dialog-body">
        <div class="pro-dialog-title">{{ text.unpaidOrderTitle }}</div>
        <div class="pro-dialog-desc">{{ text.unpaidOrderDesc }}</div>
        <div class="pro-dialog-foot">
          <span class="btn-pill btn-outline" @click="$emit('go-pay')">{{
            text.goPay
          }}</span>
          <span class="btn-pill btn-primary" @click="$emit('continue-order')">{{
            text.continueOrder
          }}</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { PRO_PLAN_TEXT } from "@/consts/proPlan";

/**
 * 专业版支付相关弹框集合
 * 通过 .sync 控制显隐；按钮行为以事件抛出，由页面决定后续逻辑。
 */
export default {
  name: "PayDialogs",
  props: {
    confirmVisible: { type: Boolean, default: false },
    queryVisible: { type: Boolean, default: false },
    failedVisible: { type: Boolean, default: false },
    unpaidVisible: { type: Boolean, default: false },
  },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      spinnerIcon: require("@/assets/images/pro-plan/plan/pay-query-spinner.png"),
      failedIcon: require("@/assets/images/pro-plan/plan/pay-failed-icon.png"),
    };
  },
  watch: {
    // el-dialog 依赖 CSS 动画的 animationend 来收尾（v-show 的 display 由 transition 回调设置）。
    // 若宿主环境暂停了动画时间轴（后台标签页 / 内嵌 webview 节流 / 系统休眠等），
    // animationend 不触发，弹框会永久残留。这里在关闭后做超时兜底：
    // 正常环境 300ms 动画早已结束并隐藏，兜底不会生效；异常环境则强制隐藏。
    confirmVisible(v) {
      if (!v) this.forceHideIfStuck("confirmDialog");
    },
    queryVisible(v) {
      if (!v) this.forceHideIfStuck("queryDialog");
    },
    failedVisible(v) {
      if (!v) this.forceHideIfStuck("failedDialog");
    },
    unpaidVisible(v) {
      if (!v) this.forceHideIfStuck("unpaidDialog");
    },
  },
  computed: {
    confirmVisibleSync: {
      get() {
        return this.confirmVisible;
      },
      set(v) {
        this.$emit("update:confirmVisible", v);
      },
    },
    queryVisibleSync: {
      get() {
        return this.queryVisible;
      },
      set(v) {
        this.$emit("update:queryVisible", v);
      },
    },
    failedVisibleSync: {
      get() {
        return this.failedVisible;
      },
      set(v) {
        this.$emit("update:failedVisible", v);
      },
    },
    unpaidVisibleSync: {
      get() {
        return this.unpaidVisible;
      },
      set(v) {
        this.$emit("update:unpaidVisible", v);
      },
    },
  },
  methods: {
    onCancel() {
      this.$emit("cancel");
    },
    // 关闭动画未正常结束时强制隐藏（详见 watch 中的说明）
    forceHideIfStuck(refName) {
      setTimeout(() => {
        const dialog = this.$refs[refName];
        const el = dialog && dialog.$el;
        if (!el) return;
        // 已由 transition 正常收尾（display:none）则无需处理
        if (el.style.display === "none") return;
        // 动画仍在进行中则再等一轮
        const anims = el.getAnimations ? el.getAnimations() : [];
        if (anims.some((a) => a.playState === "running" && a.currentTime > 0)) {
          this.forceHideIfStuck(refName);
          return;
        }
        el.style.display = "none";
      }, 500);
    },
  },
};
</script>

<style lang="scss">
// 弹框为 append-to-body，样式需非 scoped
.pro-dialog {
  border-radius: 6px;
  .el-dialog__header {
    padding: 0;
  }
  .el-dialog__body {
    padding: 0;
  }
}
.pro-dialog-body {
  padding: 28px 28px 20px;
}
.pro-dialog-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}
.pro-dialog-desc {
  margin-top: 36px;
  font-size: 16px;
  color: #888;
  line-height: 22px;
  &.center {
    text-align: center;
    margin-top: 20px;
  }
}
.pro-dialog-foot {
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
// 胶囊按钮（对齐设计稿：取消/查看订单为浅底绿字，我已支付/重新支付为绿底白字）
.btn-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 20px;
  box-sizing: border-box;
  border-radius: 30px;
  font-size: 15px;
  cursor: pointer;
  &.btn-outline {
    color: #0fa87b;
    background: #fff;
    border: 1px solid #ebeef5;
  }
  &.btn-primary {
    color: #fff;
    background: #64c3a4;
  }
}
.query-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 28px 44px;
  .query-title {
    margin-top: 20px;
    font-size: 20px;
    font-weight: 700;
    color: #333;
    line-height: 1.2;
  }
}
// 查询中图标（设计稿切图，自身为环形，加旋转动画）
.query-spinner {
  width: 54px;
  height: 54px;
  display: block;
  animation: pro-spin 1s linear infinite;
}
@keyframes pro-spin {
  to {
    transform: rotate(360deg);
  }
}
// 支付失败：图标 + 标题 + 描述 居中排布
.center-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 28px 28px;
}
.fail-icon {
  width: 46px;
  height: 46px;
  display: block;
}
.pro-dialog-title.center {
  margin-top: 16px;
  text-align: center;
}
.pro-dialog-desc.center {
  margin-top: 10px;
  text-align: center;
}
</style>
